import { useState, useEffect } from 'react';
import { getImageProps } from '@/utils/image-responsive';
import type { UploadFile } from '@edwin-group/cj-graphql/generated/graphql';

type ImageInput = UploadFile | { url: string; alternativeText: string } | undefined;

export type ImageSize = 'thumbnail' | 'small' | 'medium' | 'large' | 'original';

interface ResponsiveImageHookResult {
  src?: string;
  alt?: string;
}

const cache = new Map<string, ResponsiveImageHookResult>();

export function useResponsiveImage(image: ImageInput, size: ImageSize = 'large'): ResponsiveImageHookResult {
  const [imageProps, setImageProps] = useState<ResponsiveImageHookResult>({
    src: undefined,
    alt: undefined,
  });

  useEffect(() => {
    if (!image) {
      setImageProps({ src: undefined, alt: undefined });
      return;
    }

    const key = `${JSON.stringify(image)}_${size}`;

    if (cache.has(key)) {
      setImageProps(cache.get(key)!);
      return;
    }

    let isMounted = true;

    async function loadImageProps() {
      const { src, alt } = await getImageProps(image, size);
      if (!isMounted) return;

      // Update state and cache
      const result = { src, alt };
      setImageProps(result);
      cache.set(key, result);
    }

    loadImageProps();

    return () => {
      isMounted = false;
    };
  }, [image, size]);

  return imageProps;
}
